// ═══════════════════════════════════════════════════════
// security.js — Frontend OWASP Top 10 Protection Layer
// Every input entering the app passes through here.
// Backend (Django) applies a second identical layer.
// ═══════════════════════════════════════════════════════

// ─── A1: Injection / XSS — strip all HTML & script tags ───
export const sanitizeString = (input) => {
  if (input === null || input === undefined) return '';
  return String(input)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')           // strip all HTML tags
    .replace(/javascript:/gi, '')      // block JS protocol
    .replace(/on\w+\s*=/gi, '')        // block inline event handlers
    .replace(/data:/gi, '')            // block data URIs
    .replace(/vbscript:/gi, '')        // block VBScript
    .trim();
};

// ─── A1: SQL Injection patterns — detect suspicious input ───
const SQL_PATTERNS = [
  /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|FETCH|DECLARE)\b)/i,
  /(--|;|\/\*|\*\/|xp_|CHAR\(|NCHAR\()/i,
  /('|('')|(\b(OR|AND)\b\s+\d+\s*=\s*\d+))/i,
];

export const containsSQLInjection = (input) =>
  SQL_PATTERNS.some(p => p.test(String(input)));

// ─── Combined sanitize + injection check ───
export const secureInput = (input, maxLength = 500) => {
  const clean = sanitizeString(input).slice(0, maxLength);
  if (containsSQLInjection(clean)) return '';   // silently reject
  return clean;
};

// ─── A2: Broken Auth — validate JWT structure client-side ───
export const isValidJWT = (token) => {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  return parts.length === 3 && parts.every(p => p.length > 0);
};

// ─── A3: Sensitive data — never log tokens or passwords ───
export const safeLog = (label, data) => {
  if (process.env.NODE_ENV !== 'development') return;
  const redacted = { ...data };
  ['password', 'token', 'access', 'refresh', 'secret', 'key'].forEach(k => {
    if (redacted[k]) redacted[k] = '[REDACTED]';
  });
  console.log(`[${label}]`, redacted);
};

// ─── A5: Security Misconfiguration — Content Security Policy helper ───
// Applied via <meta> in index.html — enforced here for runtime checks
export const isSafeURL = (url) => {
  if (!url) return false;
  try {
    const u = new URL(url);
    return ['https:', 'http:'].includes(u.protocol) &&
      !url.includes('javascript:') &&
      !url.includes('data:');
  } catch { return false; }
};

// ─── A7: XSS — safe HTML entity encoder for display ───
export const encodeHTML = (str) =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

// ─── A9: Rate limiting (client side — real limit on backend) ───
const rateLimitMap = new Map();
export const checkRateLimit = (key, maxCalls = 5, windowMs = 60000) => {
  const now   = Date.now();
  const entry = rateLimitMap.get(key) || { count: 0, start: now };
  if (now - entry.start > windowMs) { rateLimitMap.set(key, { count: 1, start: now }); return true; }
  if (entry.count >= maxCalls) return false;
  rateLimitMap.set(key, { ...entry, count: entry.count + 1 });
  return true;
};

// ─── Turkish VKN (10-digit tax number) checksum ───
export const isValidVKN = (v) => {
  if (!/^\d{10}$/.test(v)) return false;
  const d = String(v).split('').map(Number);
  let total = 0;
  for (let i = 0; i < 9; i++) {
    const tmp = (d[i] + (9 - i)) % 10;
    if (tmp !== 0) {
      const p = (tmp * Math.pow(2, 9 - i)) % 9;
      total += p !== 0 ? p : 9;
    }
  }
  const check = (10 - (total % 10)) % 10;
  return check === d[9];
};

// ─── Turkish TC kimlik no (11 digits) checksum — a şahıs işletmesi's tax id ───
export const isValidTCKN = (v) => {
  if (!/^[1-9]\d{10}$/.test(v)) return false;
  const d = String(v).split('').map(Number);
  const odd = d[0] + d[2] + d[4] + d[6] + d[8];
  const even = d[1] + d[3] + d[5] + d[7];
  if (((odd * 7) - even) % 10 !== d[9]) return false;
  return d.slice(0, 10).reduce((a, b) => a + b, 0) % 10 === d[10];
};

// ─── Vergi levhası upload: PDF / JPG / PNG, max 5 MB (server re-checks) ───
export const TAX_DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
export const checkTaxDocument = (file) => {
  if (!file) return 'Vergi levhası yüklemek zorunludur.';
  const okType = /\.(pdf|jpe?g|png)$/i.test(file.name || '') ||
                 ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type);
  if (!okType) return 'Vergi levhası PDF, JPG veya PNG olmalıdır.';
  if (file.size > 5 * 1024 * 1024) return 'Vergi levhası en fazla 5 MB olabilir.';
  return '';
};

// ─── Validators ───
export const validators = {
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && v.length <= 254,

  password: (v) => ({
    valid:    /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(v),
    message:  'Şifre en az 8 karakter, 1 büyük harf, 1 küçük harf, 1 rakam ve 1 özel karakter (!@#$%^&*) içermelidir.',
  }),

  phone: (v) => /^(\+90|0)?[5][0-9]{9}$/.test(v.replace(/\s/g, '')),

  // VKN (10 digits) or, for a şahıs işletmesi, TC kimlik no (11 digits)
  taxNo: (v) => isValidVKN(v) || isValidTCKN(v),

  vin: (v) => /^[A-HJ-NPR-Z0-9]{17}$/i.test(v),

  name: (v) => v.length >= 2 && v.length <= 100 && !/[<>{}()\[\]]/.test(v),

  oem: (v) => /^[A-Za-z0-9\-\/\s\.]{3,30}$/.test(v),
};

// ─── Sanitize entire form object at once ───
export const sanitizeForm = (formObj, fieldLimits = {}) => {
  const out = {};
  for (const [key, val] of Object.entries(formObj)) {
    const limit = fieldLimits[key] || 500;
    out[key] = typeof val === 'string' ? secureInput(val, limit) : val;
  }
  return out;
};

// ─── CSRF token helper (reads from Django cookie) ───
export const getCSRFToken = () => {
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? match[1] : '';
};

// ─── Secure storage wrapper (never store sensitive data in localStorage) ───
export const secureStorage = {
  setToken: (token) => {
    if (!isValidJWT(token)) { console.error('Invalid JWT — not stored'); return; }
    // httpOnly cookie preferred (set by Django) — this is fallback only
    sessionStorage.setItem('_t', token);
  },
  getToken: () => sessionStorage.getItem('_t'),
  clearToken: () => sessionStorage.removeItem('_t'),
};